'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { isEqual } from 'lodash'
import { v4 as uuid } from 'uuid'
import { Button, Cell, Divider, Icon, Navigation, Text } from '@vinted/web-ui'
import { ArrowLeft24 } from '@vinted/monochrome-icons'

import { useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import {
  flattenDynamicFilterOptions,
  transformDynamicFilterDefaultOptions,
  transformDynamicFilterGroupOptions,
  transformDynamicFilterNavigationalOptions,
  DynamicFilterOptionModel,
  ExperimentModel,
  FilterCatalogModel,
  LinksModel,
  DynamicExposeType,
  OptionType,
  SelectionType,
  Filter,
  trackExperimentsByType,
  SELECT_LARGER_DROPDOWN_MIN_WIDTH,
  SELECT_LARGER_DROPDOWN_MAX_HEIGHT,
  TranslationModel,
} from '@marketplace-web/catalog/catalog-data'
import {
  useCatalogContext,
  clickFilterEvent,
  DynamicFilterReducerAction,
} from '@marketplace-web/catalog/catalog-page-data'
import {
  userClickDynamicFilterOptionEvent,
  userViewDynamicFilterOptionEvent,
} from '@marketplace-web/catalog/catalog-filters-data'

import SelectableItemList, {
  RenderItemProps,
  SelectableListItem,
} from '../../../SelectableItemList'
import FilterCatalogList from '../../../FilterCatalogList/FilterCatalogList'
import FilterDropdown from '../../../FilterDropdown'
import FilterGroupList from '../../../FilterGroupList/FilterGroupList'
import DynamicFilterCell from '../../common/DynamicFilterCell'
import FilterLoader from '../../common/FilterLoader'
import FilterNoResults from '../../common/FilterNoResults'
import FilterSelectionButton from '../../../FilterSelectionButton'

type ListOptionItem = SelectableListItem<DynamicFilterOptionModel>

type Props = {
  type: string
  title: string
  selectionType: SelectionType
  isSelectionHighlighted?: boolean
  isHidden?: boolean
  experiments?: Array<ExperimentModel>
  rootOptionType: OptionType
  links?: Array<LinksModel>
  options: Array<DynamicFilterOptionModel>
  selectedIds: Array<number>
  fromScreen: string
  totalItemsCount?: number | null
  totalCountMax?: number | null
  isAsyncFacet?: boolean
  isLazy?: boolean
  isLoading?: boolean
  translations?: TranslationModel
  onFilterClose: () => void
  onFilterOpen: (type: string) => void
  onSubmit: () => void
  onListOptionSelect: (id: number) => void
}

const ListFilterDropdown = ({
  type,
  title,
  selectionType,
  isSelectionHighlighted,
  isHidden,
  experiments,
  rootOptionType,
  links,
  options,
  selectedIds,
  fromScreen,
  totalItemsCount,
  totalCountMax,
  isAsyncFacet,
  isLazy,
  isLoading,
  translations,
  onFilterClose,
  onFilterOpen,
  onSubmit,
  onListOptionSelect,
}: Props) => {
  const { track } = useTracking()
  const translate = useTranslate()
  const trackAbTest = useTrackAbTestCallback()
  const {
    state: {
      items: { searchTrackingParams },
    },
    dispatch,
  } = useCatalogContext()
  const { globalSearchSessionId, globalCatalogBrowseSessionId } = searchTrackingParams
  const searchSessionId = searchTrackingParams.sessionId
  const seenItems = useRef<Array<number>>([])
  const [selectedCatalogCode, setSelectedCatalogCode] = useState<string | null>(null)
  const [highlightedSelectedIds, setHighlightedSelectedIds] = useState<Array<number>>(selectedIds)
  const [forceClose, setForceClose] = useState(false)
  const [initialSelectedIds, setInitialSelectedIds] = useState<Array<number>>(selectedIds)
  const filteringSessionId = useMemo(() => uuid(), [])
  const showNoResult = !!isLazy && options.length === 0

  useEffect(() => {
    if (experiments) trackExperimentsByType(experiments, DynamicExposeType.View, trackAbTest)
  }, [trackAbTest, experiments])

  useEffect(() => {
    if (rootOptionType === OptionType.Group && !selectedCatalogCode) {
      setSelectedCatalogCode(OptionType.Root)
    } else if (
      rootOptionType === OptionType.Navigational &&
      selectedCatalogCode === OptionType.Root
    ) {
      setSelectedCatalogCode(null)
    }
  }, [rootOptionType, selectedCatalogCode])

  const flattedOptions = useMemo(() => {
    return flattenDynamicFilterOptions(options)
  }, [options])

  const listItems = useMemo(() => {
    return transformDynamicFilterDefaultOptions(
      options,
      rootOptionType,
      isSelectionHighlighted,
      highlightedSelectedIds,
    )
  }, [options, rootOptionType, isSelectionHighlighted, highlightedSelectedIds])

  const navigationalItems = useMemo(() => {
    return transformDynamicFilterNavigationalOptions(
      options,
      rootOptionType,
      isSelectionHighlighted,
      highlightedSelectedIds,
    )
  }, [options, rootOptionType, isSelectionHighlighted, highlightedSelectedIds])

  const getGroupItems = (catalogCode: string) => {
    const currentCatalog = navigationalItems.find(({ code }) => code === catalogCode)
    if (currentCatalog) {
      return currentCatalog.matchingGroups
    }

    return transformDynamicFilterGroupOptions(
      options,
      rootOptionType,
      isSelectionHighlighted,
      highlightedSelectedIds,
    )
  }

  const getCurrentPosition = (id: number) =>
    flattedOptions.findIndex(option => option.id === id) + 1

  const handleOnFilterClick = () => {
    track(
      clickFilterEvent({
        filter: Filter.Dynamic,
        screen: 'search_filter',
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        type,
        code: type,
      }),
    )

    if (experiments) trackExperimentsByType(experiments, DynamicExposeType.Click, trackAbTest)
  }

  const handleItemClick = (id: number, position: number, itemsCount?: number) => {
    onListOptionSelect(id)

    track(
      userClickDynamicFilterOptionEvent({
        id,
        type,
        code: type,
        position,
        isSelected: !selectedIds.includes(id),
        itemsCount,
        filteringSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchSessionId,
        fromScreen,
      }),
    )

    if (experiments) trackExperimentsByType(experiments, DynamicExposeType.Apply, trackAbTest)
  }

  const getHandleItemView = (item: ListOptionItem) => (inView: boolean) => {
    if (!inView) return

    const id = Number(item.id)
    if (seenItems.current.includes(id)) return

    const position = getCurrentPosition(id)

    track(
      userViewDynamicFilterOptionEvent({
        id,
        type,
        code: type,
        position,
        itemsCount: item.data?.itemsCount,
        filteringSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchSessionId,
        fromScreen,
      }),
    )

    seenItems.current = [...seenItems.current, id]
  }

  const handleDefaultItemClick = (item: ListOptionItem) => {
    const id = Number(item.id)
    const position = getCurrentPosition(id)
    const itemsCount = item.data?.itemsCount

    handleItemClick(id, position, itemsCount)
  }

  const handelGroupItemClick = (id: number) => {
    const position = getCurrentPosition(id)

    handleItemClick(id, position)
  }

  const handelNavItemClick = (catalog: FilterCatalogModel) => {
    setSelectedCatalogCode(catalog.code)
  }

  const handleOpenDropdown = () => {
    seenItems.current = []
    setForceClose(false)
    setInitialSelectedIds(selectedIds)

    onFilterOpen(type)
  }

  const handleCloseDropdown = () => {
    setHighlightedSelectedIds(selectedIds)
    setForceClose(true)
    onSubmit()
  }

  const handleAfterClose = () => {
    const wasForceClosed = forceClose
    onFilterClose()
    setForceClose(false)
    setHighlightedSelectedIds(selectedIds)

    if (!isEqual(selectedIds, initialSelectedIds) && !wasForceClosed) {
      // the ui state for filters will set to idle till the fetch is being started
      dispatch({
        type: DynamicFilterReducerAction.FETCH_DYNAMIC_FILTERS_IDLE,
        payload: undefined,
      })

      onSubmit()
    }
  }

  const handleBackClick = () => {
    setHighlightedSelectedIds(selectedIds)
    setSelectedCatalogCode(null)
  }

  const renderNavigationHeader = () => {
    const filter = navigationalItems.find(catalog => catalog.code === selectedCatalogCode)
    if (!filter || navigationalItems.length === 1) return null

    return (
      <>
        <Navigation
          left={
            <Button
              styling={Button.Styling.Flat}
              theme="amplified"
              icon={<Icon name={ArrowLeft24} />}
              onClick={handleBackClick}
              testId={`${type}-dropdown-go-back`}
              aria={{
                'aria-label': translate('catalog.filters.a11y.actions.back'),
              }}
            />
          }
          body={filter.title}
        />
        <Divider />
      </>
    )
  }

  const renderItemCell = ({
    item,
    itemElementProps,
  }: RenderItemProps<DynamicFilterOptionModel>) => (
    <DynamicFilterCell
      key={item.id}
      type={type}
      item={item}
      itemElementProps={itemElementProps}
      handleItemView={getHandleItemView(item)}
    />
  )

  const renderDefaultDropdown = () => (
    <>
      <SelectableItemList
        name={`${type}_ids`}
        isMultiSelect={selectionType === SelectionType.Multi}
        items={listItems}
        selected={selectedIds}
        renderItem={renderItemCell}
        onItemClick={handleDefaultItemClick}
        disableButtonClicks
      />
      {links?.length && (
        <>
          <Divider />
          <Cell>
            {links.map(link => (
              <Text key={`${type}_link`} html text={link.body} as="p" />
            ))}
          </Cell>
        </>
      )}
    </>
  )

  const effectiveCatalogCode =
    rootOptionType === OptionType.Group && selectedCatalogCode === null
      ? OptionType.Root
      : selectedCatalogCode

  const renderNavigationalDropdown = () => {
    if (rootOptionType === OptionType.Group) {
      return (
        <FilterGroupList
          name={`${type}_ids`}
          isMultiSelect={selectionType === SelectionType.Multi}
          renderItem={renderItemCell}
          groups={getGroupItems(OptionType.Root)}
          selectedIds={selectedIds}
          onItemClick={handelGroupItemClick}
        />
      )
    }

    return effectiveCatalogCode ? (
      <FilterGroupList
        name={`${type}_ids`}
        isMultiSelect={selectionType === SelectionType.Multi}
        renderItem={renderItemCell}
        groups={getGroupItems(effectiveCatalogCode)}
        selectedIds={selectedIds}
        onItemClick={handelGroupItemClick}
      />
    ) : (
      <FilterCatalogList catalogs={navigationalItems} onItemClick={handelNavItemClick} />
    )
  }

  const renderDropdown = () => {
    if (rootOptionType === OptionType.Group || rootOptionType === OptionType.Navigational) {
      return renderNavigationalDropdown()
    }

    return renderDefaultDropdown()
  }

  if (isHidden) return null

  return (
    <div className="u-ui-margin-right-regular u-ui-margin-bottom-regular">
      <FilterDropdown
        title={title}
        areAnyOfItemsSelected={!!selectedIds.length}
        testId={`catalog--${type}-filter`}
        minWidth={SELECT_LARGER_DROPDOWN_MIN_WIDTH}
        onFilterClick={handleOnFilterClick}
        onOpen={handleOpenDropdown}
        onClose={handleAfterClose}
        useForceClose={forceClose}
        useChipAsTrigger
      >
        {isLoading ? (
          <FilterLoader />
        ) : (
          <>
            <div className="u-ui-padding-vertical-x-small">
              {renderNavigationHeader()}
              {showNoResult ? (
                <FilterNoResults translations={translations} />
              ) : (
                <ScrollableArea maxHeight={SELECT_LARGER_DROPDOWN_MAX_HEIGHT}>
                  {renderDropdown()}
                </ScrollableArea>
              )}
            </div>
            <FilterSelectionButton
              onSubmit={handleCloseDropdown}
              totalItemsCount={totalItemsCount}
              maxItemsCount={totalCountMax}
              isAsync={isAsyncFacet}
            />
          </>
        )}
      </FilterDropdown>
    </div>
  )
}

export default ListFilterDropdown
